import { notificationsSseBaseUrl } from '~/constants/config/env';
import { useAuthStore } from '~/store/auth.store';
import { useLocaleStore } from '~/store/locale.store';

/** SSE base URL — see `notificationsSseBaseUrl` in `~/constants/config/env.ts` for rationale. */
const notificationsPrefix = notificationsSseBaseUrl;

/**
 * Stream-level error carrying the upstream HTTP status and any Retry-After
 * hint. Consumers branch by `.status` (duck-typed) so the check survives
 * module-graph duplication (HMR, dual ESM/CJS, vi.mock without importActual).
 */
export class NotificationsStreamError extends Error {
  readonly status: number;
  /** Server-suggested retry delay in seconds, parsed from the Retry-After header. Undefined when absent or unparseable. */
  readonly retryAfterSeconds?: number;
  constructor(status: number, message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = 'NotificationsStreamError';
    this.status = status;
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Parse a Retry-After header value into seconds. Spec allows either a
 * delta-seconds integer or an HTTP-date; both are supported here. Returns
 * undefined when the header is absent or malformed.
 */
export function parseRetryAfter(headerValue: string | null): number | undefined {
  if (!headerValue) return undefined;
  const trimmed = headerValue.trim();
  // delta-seconds form
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number.parseInt(trimmed, 10);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
  }
  // HTTP-date form
  const dateMs = Date.parse(trimmed);
  if (Number.isNaN(dateMs)) return undefined;
  const deltaMs = dateMs - Date.now();
  return deltaMs > 0 ? Math.ceil(deltaMs / 1000) : 0;
}

/** Render-shaped card emitted alongside every SSE notification. */
export interface NotificationStreamCard {
  title: string;
  /** Body copy shown under the title on the toast (wire field is `body`). */
  body?: string;
  /** Lucide icon name (kebab-case), e.g. "rocket" — drives the toast glyph. */
  icon?: string;
}

/**
 * Wire shape for a single notification arriving on the SSE stream. Matches
 * the live payload from `GET /notifications/stream` — additive backend
 * fields are tolerated since the parser only reads what's listed here.
 */
export interface NotificationStreamEvent {
  notification_id: string;
  configuration_id?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  received_at?: string;
  card: NotificationStreamCard;
  /** True when the backend is replaying a historical event (e.g. on reconnect). */
  replay?: boolean;
  /** True when the event was triggered by the channel-config "Send Test" button. */
  test?: boolean;
}

export interface NotificationsStreamOptions {
  /** Path appended to the notifications service prefix (e.g. `notifications/stream`). */
  url: string;
  /** Fired once after the server returns 200 — used by the consumer to reset reconnect backoff. */
  onOpen?: () => void;
  /** Fired for each parsed `notification` event. `ping` heartbeats are filtered out. */
  onNotification: (event: NotificationStreamEvent) => void;
  /** Stream-level error (network down, 401, malformed response). Called once before the controller aborts. */
  onError: (error: Error) => void;
  /** Called when the server closes the stream cleanly. */
  onComplete: () => void;
  /** Optional external abort signal. */
  signal?: AbortSignal;
}

/**
 * Opens a GET-based SSE stream against the notification service. Mirrors
 * `src/api/sse-client.ts` (which is POST + chat-shaped) but: HTTP method is
 * GET (no body), URL prefix is the notification service, and only the
 * `notification` / `message` SSE event types are forwarded.
 *
 * Auth + tenant headers (`Authorization`, `X-Subsidiary-ID`, `Accept-Language`)
 * are read directly from the Zustand stores at connection time — same pattern
 * the chat SSE client uses.
 */
export function createNotificationsStream(options: NotificationsStreamOptions): AbortController {
  const { url, onOpen, onNotification, onError, onComplete, signal } = options;
  const controller = new AbortController();

  if (signal) {
    signal.addEventListener(
      'abort',
      () => {
        controller.abort();
      },
      { once: true },
    );
  }

  const fullUrl = `${notificationsPrefix}/${url}`;
  const { user, accessToken } = useAuthStore.getState();
  const tenantId = user?.activeTenantId;
  const locale = useLocaleStore.getState().activeLocale;

  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Accept-Language': locale,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (tenantId) headers['X-Subsidiary-ID'] = tenantId;

  void (async () => {
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });

      if (!response.ok) {
        const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
        throw new NotificationsStreamError(
          response.status,
          `SSE stream failed: ${String(response.status)} ${response.statusText}`,
          retryAfter,
        );
      }
      if (!response.body) {
        throw new Error('Response body is null — streaming not supported');
      }

      onOpen?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer.trim()) processBlock(buffer, onNotification);
          onComplete();
          return;
        }
        buffer += decoder.decode(value, { stream: true });

        // Standard SSE framing: events are separated by a blank line (\n\n).
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          if (part.trim()) processBlock(part, onNotification);
        }
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      onError(err instanceof Error ? err : new Error(String(err)));
    }
  })();

  return controller;
}

/**
 * Parse one SSE event block (concatenated `event:` + `data:` lines) and
 * forward it as a `NotificationStreamEvent` if it looks well-formed.
 * Unknown event types and malformed JSON are silently dropped.
 */
function processBlock(
  block: string,
  onNotification: (event: NotificationStreamEvent) => void,
): void {
  let eventType = 'message';
  let dataStr = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) eventType = line.slice(6).trim();
    else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
  }

  if (eventType === 'ping' || !dataStr) return;
  // Backend uses dot-namespaced event types (e.g. `notification.inbox.notification`,
  // `notification.alert.<x>`). Accept anything under the `notification.` namespace
  // plus the bare `notification` / `message` names for forward compat.
  if (
    eventType !== 'notification' &&
    eventType !== 'message' &&
    !eventType.startsWith('notification.')
  ) {
    return;
  }

  try {
    // Parse as Partial first so the runtime guard for required fields
    // (`notification_id`, `card.title`) isn't redundant in TypeScript's eyes.
    const raw = JSON.parse(dataStr) as Partial<NotificationStreamEvent>;
    if (raw.notification_id && raw.card?.title) {
      onNotification(raw as NotificationStreamEvent);
    }
  } catch {
    // Malformed JSON — backend bug, not user-actionable.
  }
}
